import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import { LoadingIcon } from '@/components/icons';
import { MANAGEMENT_PAGE_SIZE as pageSize } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { scheduledLessonResourceQuerykeys } from '@/data/services/querykeys';
import {
  createScheduledLessonResource,
  removeScheduledLessonResource,
  ResourceFields,
  updateScheduledLessonResource,
} from '@/data/services/_deprecated/resourceServices';
import Resource from '@/models/_deprecated/Resource';
import ScheduledLesson from '@/models/ScheduledLesson';
import User from '@/models/User';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import DynamicResourceForm from '../form/DynamicResourceForm';
import ScheduledLessonResourceEditor from '../form/ScheduledLessonResourceEditor';

export type UpdateResourceParms = {
  resourceId: number;
  body: Partial<ResourceFields>;
};

type Props = {
  user: User;
  scheduledLesson: ScheduledLesson;
  slugCourseName: string;
};

export default function ScheduledLessonResourceContainer(props: Props) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { user, scheduledLesson, slugCourseName } = props;

  const scheduledLessonId = scheduledLesson.id;

  const listRef = useRef<HTMLUListElement>(null);

  const filters = {
    pageSize,
  };

  const { queryKey, queryFn } = scheduledLessonResourceQuerykeys.list(
    scheduledLessonId,
    filters,
  )._ctx.infinity;

  const {
    results: resources,
    isInitialLoading: loadingResources,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    invalidate,
    cacheManager,
  } = useInfiniteService({
    queryKey,
    queryFn,
  });

  const createResource = async (body: Partial<ResourceFields>) => {
    let uploadingProgress = 0;
    const tempId = Date.now();

    const auxResource = new Resource({
      ...body,
      id: tempId,
      uploadingProgress,
      uploading: true,
    });

    cacheManager.add(auxResource, { position: 'end' });

    return {
      falseId: auxResource.id,
      resource: await createScheduledLessonResource(
        scheduledLesson.id,
        body,
        progressEvent => {
          if (uploadingProgress === 0) {
            listRef.current?.scrollTo({ top: 9999, behavior: 'smooth' });
          }
          uploadingProgress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
          );

          cacheManager.update({
            id: auxResource.id,
            uploadingProgress,
            uploading: true,
          });
        },
      ),
    };
  };

  const { mutateAsync: create, isLoading: loadingCreate } = useMutation(
    createResource,
    {
      onSuccess: async data => {
        alert.success(t('saved'));
        cacheManager.update(
          {
            ...data.resource,
            id: data.falseId,
            uploading: false,
            uploadingProgress: 100,
          },
          { newId: data.resource.id },
        );
      },
      onError: async error => {
        alert.error(getErrorMessage(error));
        await invalidate?.();
      },
    },
  );

  const updateResource = async (params: UpdateResourceParms) => {
    const { resourceId, body } = params;
    let uploadingProgress = 0;

    cacheManager.update({
      id: resourceId,
      uploading: true,
      uploadingProgress: 0,
    });

    await updateScheduledLessonResource(
      scheduledLesson.id,
      resourceId,
      body,
      progressEvent => {
        uploadingProgress = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
        );

        cacheManager.update({
          uploadingProgress,
          uploading: true,
          id: resourceId,
        });
      },
    );
  };

  const { mutateAsync: update, isLoading: loadingUpdate } = useMutation(
    updateResource,
    {
      onSuccess: async () => {
        alert.success(t('saved'));
        await invalidate?.();
      },
      onError: async error => {
        alert.error(getErrorMessage(error));
        await invalidate?.();
      },
    },
  );

  const removeResource = async (resourceId: number) => {
    await removeScheduledLessonResource(scheduledLessonId, resourceId);
  };

  const { mutateAsync: remove, isLoading: loadingRemove } = useMutation(
    removeResource,
    {
      onSuccess: async () => {
        alert.success(t('saved'));
        await invalidate?.();
      },
      onError: async error => {
        alert.error(getErrorMessage(error));
        await invalidate?.();
      },
    },
  );

  const loading = loadingCreate || loadingUpdate || loadingRemove;

  if (!user) return null;

  return (
    <section className="flex flex-col w-full gap-3.5">
      <ConditionalRenderer condition={loadingResources}>
        <LoadingIcon className="w-20 self-center text-secondary-content" />
      </ConditionalRenderer>
      <ConditionalRenderer condition={error}>
        <Text text={getErrorMessage(error)} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={resources.length && !loadingResources && !error}
      >
        <InfinityList
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onReachEnd={fetchNextPage}
          className="gap-2 max-h-[20rem] overflow-x-hidden"
          scroll
          ref={listRef}
        >
          {resources.map(resource => (
            <ScheduledLessonResourceEditor
              key={resource.id}
              klassId={scheduledLesson.klass}
              lessonId={scheduledLesson.lesson?.id}
              removeResource={resourceId => remove(resourceId)}
              resource={resource}
              slugCourseName={slugCourseName}
              updateResource={params => update(params)}
              loading={loading}
            />
          ))}
        </InfinityList>
      </ConditionalRenderer>

      <DynamicResourceForm
        loading={loading}
        user={user}
        createResource={async body => {
          create(body);
        }}
        editingAllowed
      />
    </section>
  );
}
